import { useCallback, useState } from "react";
import { formatCryptocomSymbol, useCryptocomWsConnection } from "@/hooks/exchanges/direct-ws/cryptocom";
import type { CryptocomMarkPriceMessage } from "@/hooks/exchanges/direct-ws/cryptocom/types";

interface UseCryptocomMarkPriceWSResult {
  markPrice: number | undefined;
  isConnected: boolean;
  error: string | null;
}

/**
 * Hook for subscribing to Crypto.com mark price via shared WebSocket.
 * Uses the mark.{instrument_name} channel.
 */
export const useCryptocomMarkPriceWS = ({
  pairSymbol,
  enabled = true,
}: {
  pairSymbol: string | undefined;
  enabled?: boolean;
}): UseCryptocomMarkPriceWSResult => {
  const [markPrice, setMarkPrice] = useState<number | undefined>(undefined);
  const [markPriceError, setMarkPriceError] = useState<string | null>(null);

  const setup = useCallback(
    (ws: WebSocket) => {
      if (!pairSymbol) return;

      setMarkPrice(undefined);
      setMarkPriceError(null);

      const symbol = formatCryptocomSymbol(pairSymbol);
      const channel = `mark.${symbol}`;

      ws.send(JSON.stringify({ id: 1, method: "subscribe", params: { channels: [channel] } }));

      return () => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ id: 2, method: "unsubscribe", params: { channels: [channel] } }));
        }
      };
    },
    [pairSymbol],
  );

  const onMessage = useCallback(
    (msg: unknown) => {
      if (!pairSymbol) return;
      const expectedSubscription = `mark.${formatCryptocomSymbol(pairSymbol)}`;

      try {
        const typed = msg as CryptocomMarkPriceMessage;

        if (typed.result?.subscription === expectedSubscription && typed.result?.data?.[0]) {
          const price = parseFloat(typed.result.data[0].v ?? "");
          if (!isNaN(price)) {
            setMarkPrice(price);
            setMarkPriceError(null);
          }
        }
      } catch {
        setMarkPriceError("Failed to parse mark price update");
      }
    },
    [pairSymbol],
  );

  const { isConnected, error: connectionError } = useCryptocomWsConnection({
    setup,
    onMessage,
    enabled: enabled && !!pairSymbol,
  });

  return {
    markPrice,
    isConnected,
    error: markPriceError ?? connectionError,
  };
};
